/**
 * Source-of-truth pour la grille tarifaire Arpentia.
 *
 * Toute la donnée pricing vit ici. Aucun prix ni quota codé en dur dans les
 * composants — les UIs doivent consommer `plans`, `creditTopUps`, `packs` et
 * les helpers ci-dessous.
 *
 * Les prix sont **HT** (TVA non applicable — art. 293 B CGI tant que le CA
 * reste sous le seuil de franchise, note de bas de page affichée sur /pricing).
 *
 * **Plan de bascule TVA** (cf. CLAUDE.md, section "TVA / franchise art. 293 B")
 * quand le CA franchit le seuil 2026 (37 500 € HT/an services, 41 250 € en
 * tolérance) : activer Stripe Tax, décider absorber vs. répercuter (pattern
 * B2B = répercuter), repricer en TTC ici, communiquer aux abonnés (préavis
 * 1 mois). Pas urgent tant qu'on est sous le seuil — alerte Stripe Dashboard
 * à 3 000 €/mois à configurer.
 *
 * Les `priceId` Stripe pointent vers les Price créés par
 * `scripts/stripe-provision.mjs` (mode TEST pour l'instant). Le helper
 * `resolveStripePriceId` et `lookupKeyFor` s'en servent côté checkout.
 *
 * Pour le cycle "engagement", le Price Stripe est un `recurring.interval: year`
 * facturé en une fois (prix annuel = monthly × 12). L'UI affiche ça
 * transparente-ment ("Soit N € HT facturés à la souscription").
 */

export type PlanId = "starter" | "negociateur" | "agence" | "chasse";
export type PackId = "single" | "pack3" | "pack10" | "pack25";
export type BillingCycle = "engagement" | "monthly";

/** Cible marketing — utilisée par le parcours "Vous êtes ?" pour mettre en
 * avant le plan recommandé. */
export type Audience = "agent-solo" | "agence" | "promoteur" | "architecte";

export type Plan = {
  id: PlanId;
  name: string;
  /** Cible courte affichée sous le nom du plan (ex. "Agent indépendant actif"). */
  audience: string;
  /** Phrase d'accroche 1 ligne positionnant le plan (pitch commercial). */
  tagline: string;
  /** Nombre de fiches incluses par cycle de facturation. */
  ficheQuota: number;
  prices: {
    /** Prix mensuel HT avec engagement annuel (−20 %). */
    engagement: number;
    /** Prix mensuel HT sans engagement. */
    monthly: number;
  };
  /** Features mises en avant dans la card — garder 4-5 bullets max. */
  features: string[];
  /** Mis en avant visuellement avec badge "Populaire". */
  highlight?: boolean;
  /** Audiences pour lesquelles ce plan est recommandé (parcours "Vous êtes ?"). */
  recommendedFor?: Audience[];
  /** IDs Stripe — à remplir quand les produits sont créés (scaffold pour l'instant). */
  stripePriceIds: {
    engagement: string | null;
    monthly: string | null;
  };
};

export type CreditTopUp = {
  planId: PlanId;
  /** Prix unitaire HT d'une fiche add-on, ratio typique à 1,5–2× le prix
   *  marginal du plan (marge préservée sur overage). */
  unitPrice: number;
  /** Durée de validité des crédits achetés tant que l'abonnement est actif. */
  validityMonths: 12;
  /** Tailles de pré-pack proposées au checkout (à la volée = 1 par défaut). */
  prepackSizes: readonly [5, 10, 25];
  /** ID Stripe du price "metered" ou "per-unit". Scaffold. */
  stripePriceId: string | null;
};

export type Pack = {
  id: PackId;
  name: string;
  /** Nombre de fiches incluses. */
  quantity: number;
  /** Prix total HT du pack. */
  price: number;
  /** Validité en mois (0 = pas de limite). */
  validityMonths: number;
  /** Mis en avant visuellement (recommandé). */
  highlight?: boolean;
  stripePriceId: string | null;
};

// ─── Configuration globale ───────────────────────────────────────────────

/** Réduction appliquée au prix mensuel pour les plans avec engagement. */
export const ENGAGEMENT_DISCOUNT_PCT = 20;

/** Durée de validité du crédit gratuit offert à l'inscription (1 fiche, 7j). */
export const FREE_SIGNUP_CREDIT_DAYS = 7;
export const FREE_SIGNUP_CREDIT_AMOUNT = 1;

/** Devise (pour formatage + Stripe). */
export const CURRENCY = "EUR" as const;

// ─── Plans d'abonnement ──────────────────────────────────────────────────

export const plans: readonly Plan[] = [
  {
    id: "starter",
    name: "Starter",
    audience: "Architecte · agent débutant · test",
    tagline: "Pour tester Arpentia sur quelques dossiers avant d'embarquer l'équipe.",
    ficheQuota: 5,
    prices: { engagement: 44, monthly: 55 },
    features: [
      "5 fiches / mois",
      "Les 6 analyses incluses",
      "Export PDF à votre marque",
      "Support email",
    ],
    recommendedFor: ["architecte"],
    stripePriceIds: {
      engagement: "price_1TPsTzPjISkLVjL5h1MZeZyT",
      monthly: "price_1TPsTzPjISkLVjL5pD3QoP2g",
    },
  },
  {
    id: "negociateur",
    name: "Négociateur",
    audience: "Agent indépendant actif",
    tagline: "Le sweet-spot pour 1 à 2 mandats signés par mois.",
    ficheQuota: 15,
    prices: { engagement: 89, monthly: 111 },
    features: [
      "15 fiches / mois",
      "Modèles PDF personnalisés",
      "Export PDF à votre marque",
      "Crédits add-on à 14 €/fiche",
      "Support email prioritaire",
    ],
    highlight: true,
    recommendedFor: ["agent-solo"],
    stripePriceIds: {
      engagement: "price_1TPsU1PjISkLVjL5C9sRJkSq",
      monthly: "price_1TPsU0PjISkLVjL5oh9ung6Z",
    },
  },
  {
    id: "agence",
    name: "Agence",
    audience: "Équipe jusqu'à 5 agents",
    tagline: "Une vitrine unifiée pour toute l'équipe, avec branding multi-points de vente.",
    ficheQuota: 40,
    prices: { engagement: 169, monthly: 209 },
    features: [
      "40 fiches / mois",
      "Multi-utilisateurs (5)",
      "Branding multi-vitrines",
      "Tout du plan Négociateur",
      "Support prioritaire",
    ],
    recommendedFor: ["agence"],
    stripePriceIds: {
      engagement: "price_1TPsU2PjISkLVjL5nXcVf3fT",
      monthly: "price_1TPsU2PjISkLVjL5pwiubKwQ",
    },
  },
  {
    id: "chasse",
    name: "Chasse Foncière",
    audience: "Promoteur · foncier · prospection",
    tagline: "Pour industrialiser la détection de fonciers à fort potentiel.",
    ficheQuota: 100,
    prices: { engagement: 439, monthly: 549 },
    features: [
      "100 fiches / mois",
      "Accès API & export batch CSV",
      "Historique DVF enrichi",
      "Account manager dédié",
      "Tout du plan Agence",
    ],
    recommendedFor: ["promoteur"],
    stripePriceIds: {
      engagement: "price_1TPsU4PjISkLVjL5YHvKCVL8",
      monthly: "price_1TPsU3PjISkLVjL5RP9MYzi9",
    },
  },
] as const;

// ─── Crédits add-on (abonnés uniquement) ─────────────────────────────────
// Prix unitaire dégressif avec le plan — incite à monter en gamme plutôt qu'à
// empiler des packs sur Starter.

export const creditTopUps: readonly CreditTopUp[] = [
  { planId: "starter", unitPrice: 18, validityMonths: 12, prepackSizes: [5, 10, 25], stripePriceId: "price_1TPsU5PjISkLVjL5nLncjcIg" },
  { planId: "negociateur", unitPrice: 14, validityMonths: 12, prepackSizes: [5, 10, 25], stripePriceId: "price_1TPsU6PjISkLVjL5PucHKibI" },
  { planId: "agence", unitPrice: 11, validityMonths: 12, prepackSizes: [5, 10, 25], stripePriceId: "price_1TPsU7PjISkLVjL5Hp6D7hKa" },
  { planId: "chasse", unitPrice: 8, validityMonths: 12, prepackSizes: [5, 10, 25], stripePriceId: "price_1TPsU8PjISkLVjL53Lp6bMB2" },
] as const;

// ─── Packs ponctuels (non-abonnés) ───────────────────────────────────────

export const packs: readonly Pack[] = [
  { id: "single", name: "Fiche à l'unité", quantity: 1, price: 49, validityMonths: 0, stripePriceId: "price_1TPsU9PjISkLVjL53YehaMYv" },
  { id: "pack3", name: "Pack 3", quantity: 3, price: 129, validityMonths: 6, stripePriceId: "price_1TPsUAPjISkLVjL5xrmLtscb" },
  { id: "pack10", name: "Pack 10", quantity: 10, price: 349, validityMonths: 6, highlight: true, stripePriceId: "price_1TPsUBPjISkLVjL5EPdAwYBh" },
  { id: "pack25", name: "Pack 25", quantity: 25, price: 799, validityMonths: 12, stripePriceId: "price_1TPsUCPjISkLVjL5kLk1pMj4" },
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Retourne le prix mensuel HT d'un plan selon le cycle choisi. */
export function priceFor(plan: Plan, cycle: BillingCycle): number {
  return cycle === "engagement" ? plan.prices.engagement : plan.prices.monthly;
}

/** Coût par fiche au prix affiché (pour la ligne "€/fiche" des cards). */
export function pricePerFiche(plan: Plan, cycle: BillingCycle): number {
  return priceFor(plan, cycle) / plan.ficheQuota;
}

/** Formatage EUR HT — utilise le séparateur de milliers FR (espace insécable)
 * et le séparateur décimal "," pour rester cohérent avec le reste du site. */
export function formatEur(amount: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "decimal",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

/** Pour la ligne "€/fiche" — 2 décimales quand c'est pertinent. */
export function formatPerFiche(amount: number): string {
  // 4.23 → "4,23"  ·  8.80 → "8,80"  ·  49 → "49"
  const rounded = Math.round(amount * 100) / 100;
  return rounded === Math.round(rounded)
    ? formatEur(rounded, 0)
    : formatEur(rounded, 2);
}

/** Plan recommandé pour une audience donnée (parcours "Vous êtes ?"). */
export function recommendedPlanFor(audience: Audience): Plan {
  const match = plans.find((p) => p.recommendedFor?.includes(audience));
  // Fallback à "negociateur" (plan star) si pas de correspondance explicite.
  return match ?? plans.find((p) => p.id === "negociateur")!;
}

/** Résolution du priceId Stripe pour un (plan, cycle) — utilisé par le
 * `createCheckoutSession` callable. Retourne null tant que Stripe n'est pas
 * câblé, le callable répondra avec une erreur explicite dans ce cas. */
export function resolveStripePriceId(planId: PlanId, cycle: BillingCycle): string | null {
  const plan = plans.find((p) => p.id === planId);
  return plan?.stripePriceIds[cycle] ?? null;
}

/** Pack le moins cher par fiche — affiché comme point de référence concurrentiel. */
export function cheapestPerFichePlan(cycle: BillingCycle = "engagement"): { plan: Plan; perFiche: number } {
  let best = { plan: plans[0], perFiche: pricePerFiche(plans[0], cycle) };
  for (const plan of plans) {
    const perFiche = pricePerFiche(plan, cycle);
    if (perFiche < best.perFiche) best = { plan, perFiche };
  }
  return best;
}
